import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

const KEY = "astrovoyance.lang";
const Ctx = createContext(null);

// Compact bilingual string bank — every UI copy the user sees.
// Keys are semantic; missing keys fall back to the FR value.
const DICT = {
  // Nav
  "nav.oracles": ["Oracles", "Oracles"],
  "nav.barde": ["Le Barde", "The Bard"],
  "nav.astro": ["Astrocarto", "Astrocarto"],
  "nav.druide": ["Le Druide", "The Druid"],
  "nav.taverne": ["Pub du Barde", "The Bard's Pub"],
  "nav.vate": ["Le Vate", "The Vate"],
  "nav.gaia": ["Gaïa", "Gaia"],
  "nav.suantrai": ["Suantraí", "Suantraí"],
  "nav.farfadet": ["Farfadet", "Farfadet"],
  "nav.korrigan": ["Korrigan", "Korrigan"],
  "nav.grammarien": ["Grammarien", "Grammarian"],
  "nav.traditions": ["Traditions", "Traditions"],
  "nav.pricing": ["Tarifs", "Pricing"],
  "nav.subscribe": ["Abonnement", "Subscribe"],
  // Hero
  "hero.badge": ["Personnages IA · pour le divertissement", "AI characters · for entertainment"],
  "hero.title.line1a": ["La sagesse ", "Wisdom "],
  "hero.title.line1b": ["ne se prédit", "is not predicted"],
  "hero.title.line1c": [" pas.", "."],
  "hero.title.line2a": ["Elle se ", "It is "],
  "hero.title.line2b": ["vit", "lived"],
  "hero.title.line2c": [".", "."],
  "hero.subtitle": [
    "Des intelligences artificielles inspirées des bardes et druides celtes, des dieux grecs, des anges et des éléments t'accompagnent sans jamais te diriger : des suggestions bienveillantes, jamais d'ordres ni de prédictions, avec philosophie, poésie et une bonne dose d'humour pour dédramatiser et réfléchir par toi-même, te détacher et gagner en autonomie. 100 % gratuit, anonyme, pour le divertissement.",
    "Artificial intelligences inspired by Celtic bards and druids, Greek gods, angels and the elements accompany you without ever directing you: kind suggestions, never orders or predictions, with philosophy, poetry and a good dose of humour to lighten things up and think for yourself, let go and grow in autonomy. 100% free, anonymous, for entertainment.",
  ],
  "hero.cta.oracles": ["Consulter les 6 oracles", "Consult the 6 oracles"],
  "hero.cta.barde": ["Parler au Barde", "Speak to the Bard"],
  "hero.stat.oracles": ["Oracles gratuits", "Free oracles"],
  "hero.stat.perday": ["Questions par jour", "Questions per day"],
  "hero.stat.price": ["Pour la sagesse", "For wisdom"],
  "hero.email.overline": ["Votre courriel — pour tout suivre", "Your email — to track everything"],
  "hero.email.placeholder": ["votre@courriel.com", "you@email.com"],
  "hero.email.save": ["Enregistrer", "Save"],
  "hero.email.linked": ["Session liée à", "Session linked to"],
  "hero.moon": ["Nouvelle Lune", "New Moon"],
  "hero.moon.active": ["Guidance active", "Active guidance"],
  // Oracles
  "oracles.kicker": ["Six oracles · philosophie · gratuits", "Six oracles · philosophy · free"],
  "oracles.title.a": ["La sagesse", "Wisdom "],
  "oracles.title.b": ["vous appartient", "belongs to you"],
  "oracles.title.c": [".", "."],
  "oracles.subtitle": [
    "Ces oracles ne prédisent pas — ils vous ramènent à vous. Citations bouddhistes, philosophie et une pointe de taquinerie. Vingt questions par jour, pas une de plus : la sagesse aime la sobriété.",
    "These oracles do not predict — they return you to yourself. Buddhist quotes, philosophy, and a touch of teasing. Twenty questions per day, no more: wisdom loves sobriety.",
  ],
  "oracles.counter.used": ["questions posées aujourd'hui", "questions asked today"],
  "oracles.counter.remaining": ["restantes", "remaining"],
  "oracles.counter.hint": ["Ajoutez votre courriel pour suivre vos 20 questions/jour", "Add your email to track your 20 questions/day"],
  "oracles.question.placeholder": ["Votre question…", "Your question…"],
  "oracles.ask": ["Interroger l'oracle", "Consult the oracle"],
  // Oracle names & taglines
  "oracle.pendule.label": ["Pendule", "Pendulum"],
  "oracle.pendule.tagline": ["L'oscillation vous rend à votre intuition.", "The swing returns you to your intuition."],
  "oracle.boule_lumiere.label": ["Boule de Lumière", "Ball of Light"],
  "oracle.boule_lumiere.tagline": ["Une clarté qui reflète la vôtre.", "A clarity that mirrors your own."],
  "oracle.diamant.label": ["Oracle de Diamant", "Diamond Oracle"],
  "oracle.diamant.tagline": ["Une vérité tranchante et honnête.", "A sharp and honest truth."],
  "oracle.confiance.label": ["Confiance en Soi", "Self-Confidence"],
  "oracle.confiance.tagline": ["Un miroir qui rappelle votre valeur.", "A mirror that recalls your worth."],
  "oracle.gentil.label": ["Oracle Gentil", "Kind Oracle"],
  "oracle.gentil.tagline": ["Une main douce pour les jours lourds.", "A gentle hand for heavy days."],
  "oracle.farfadet.label": ["Farfadet Taquin", "Teasing Faerie"],
  "oracle.farfadet.tagline": ["Le lutin qui rit de votre besoin d'oracles.", "The imp who laughs at your need for oracles."],
  // Chats
  "chats.kicker": ["Deux voix celtiques", "Two Celtic voices"],
  "chats.title.a": ["Le", "The "],
  "chats.title.b": ["Barde", "Bard"],
  "chats.title.c": [" et le ", " and the "],
  "chats.title.d": ["Druide", "Druid"],
  "chats.title.e": [".", "."],
  "chats.subtitle": [
    "Deux compagnons qui refusent de prédire — et vous forment à votre autonomie avec humour, satire et sagesse celtique ancienne.",
    "Two companions who refuse to predict — and train you toward autonomy with humor, satire, and ancient Celtic wisdom.",
  ],
  "barde.label": ["Le Barde Celtique", "The Celtic Bard"],
  "barde.tag": ["Gratuit · Anonyme · Illimité · Taquin", "Free · Anonymous · Unlimited · Teasing"],
  "barde.empty": [
    "« Salut, âme errante. Assieds-toi près du feu — aucun nom, aucun courriel à donner. Parle, et je te répondrai par des questions. »",
    "\"Hello, wandering soul. Sit by the fire — no name, no email needed. Speak, and I'll answer with questions.\"",
  ],
  "barde.placeholder": ["Parlez au Barde…", "Speak to the Bard…"],
  "barde.loading": ["Le Barde accorde sa harpe…", "The Bard tunes his harp…"],
  "druide.label": ["Le Druide", "The Druid"],
  "druide.tag": ["Gratuit · Illimité · Tuteur érudit", "Free · Unlimited · Scholarly tutor"],
  "druide.empty": [
    "« Bienvenue dans le Nemeton. Ici, je n'oracle rien — je vous forme. Posez une question de vie. »",
    "\"Welcome to the Nemeton. Here, I oracle nothing — I train you. Ask a life question.\"",
  ],
  "druide.placeholder": ["Posez une question au Druide…", "Ask the Druid a question…"],
  "druide.loading": ["Le Druide consulte le grand chêne…", "The Druid consults the great oak…"],
  "druide.upgrade": [
    "Le Druide et l'astrocartographie sont inclus dans le Premium à 15 $/mois.",
    "The Druid and astrocartography are included in Premium at $15/mo.",
  ],
  "druide.upgrade.cta": ["Rejoindre le Nemeton", "Join the Nemeton"],
  // Astro
  "astro.kicker": ["Premium · Astrocartographie complète", "Premium · Full astrocartography"],
  "astro.title.a": ["Vos", "Your "],
  "astro.title.b": ["lignes", "planetary"],
  "astro.title.c": [" planétaires.", " lines."],
  "astro.subtitle": [
    "Une lecture astrocartographique classique inclus dans le Premium 15 $/mois. Pour une analyse complète de destination (guide voyage, immobilier, culture, énergie, numérologie, signes sidéral et chinois, MC & IC), ajoutez le forfait Destination 10 $ à la demande.",
    "A classic astrocartography reading included with Premium at $15/mo. For a full destination analysis (travel guide, real estate, culture, energy, numerology, sidereal & Chinese signs, MC & IC), add the Destination $10 package on demand.",
  ],
  "astro.birth.date": ["Date de naissance", "Birth date"],
  "astro.birth.time": ["Heure", "Time"],
  "astro.birth.place": ["Ville de naissance", "Birth city"],
  "astro.birth.place.placeholder": ["Ex : Rennes, France", "e.g. Rennes, France"],
  "astro.focus.label": ["Focus (lecture basique)", "Focus (basic reading)"],
  "astro.focus.voyage": ["Voyage", "Travel"],
  "astro.focus.vie": ["Lieu de vie", "Home place"],
  "astro.focus.amour": ["Amour", "Love"],
  "astro.focus.carriere": ["Carrière", "Career"],
  "astro.submit": ["Lecture astrocarto (Premium)", "Astrocarto reading (Premium)"],
  "astro.submit.loading": ["Consultation des astres…", "Consulting the stars…"],
  "astro.premium.required": ["Premium 15 $/mois requis pour cette lecture.", "Premium $15/mo required for this reading."],
  "astro.map.badge": ["Carte céleste", "Celestial map"],
  "astro.result.destinations": ["Destinations idéales", "Ideal destinations"],
  "astro.result.interpretation": ["Interprétation", "Interpretation"],
  // Destination add-on
  "dest.title": ["Destination complète · 10 $", "Full destination · $10"],
  "dest.subtitle": [
    "Guide voyage, immobilier, culture, énergie, numérologie, sidéral, chinois, MC/IC",
    "Travel guide, real estate, culture, energy, numerology, sidereal, Chinese, MC/IC",
  ],
  "dest.intent.voyage": ["Voyage temporaire", "Temporary travel"],
  "dest.intent.demenagement": ["Déménagement", "Moving"],
  "dest.buy": ["Acheter cette lecture · 10 $", "Buy this reading · $10"],
  "dest.loading": ["Génération du dossier complet…", "Generating full report…"],
  "dest.result.destination": ["Destination", "Destination"],
  "dest.result.guide": ["Guide de voyage", "Travel guide"],
  "dest.result.realestate": ["Immobilier & installation", "Real estate & moving in"],
  "dest.result.culture": ["Analyse culturelle", "Cultural analysis"],
  "dest.result.energy": ["Énergie du lieu / thème natal", "Place energy / natal chart"],
  "dest.result.numerology": ["Numérologie", "Numerology"],
  "dest.result.sidereal": ["Signe sidéral", "Sidereal sign"],
  "dest.result.chinese": ["Signe chinois", "Chinese sign"],
  "dest.result.mcic": ["MC & IC", "MC & IC"],
  // Pricing
  "pricing.kicker": ["Tarifs · sobriété assumée", "Pricing · deliberate sobriety"],
  "pricing.title.a": ["Un abonnement", "A "],
  "pricing.title.b": ["léger", "light"],
  "pricing.title.c": [", un add-on précis.", " subscription, a precise add-on."],
  "pricing.subtitle": [
    "La majorité de nos outils sont gratuits — parce que la sagesse se transmet plus qu'elle ne se vend. Le Premium finance l'infrastructure et l'IA druidique.",
    "Most of our tools are free — because wisdom is shared more than sold. Premium funds the infrastructure and the druidic AI.",
  ],
  "pricing.email": ["Votre courriel", "Your email"],
  "pricing.recommended": ["Recommandé", "Recommended"],
  "pricing.premium.title": ["Premium", "Premium"],
  "pricing.premium.sub": ["Abonnement mensuel", "Monthly subscription"],
  "pricing.premium.month": ["$/mois", "/mo"],
  "pricing.premium.cancel": ["Résiliation à tout moment", "Cancel anytime"],
  "pricing.premium.f1": ["Astrocartographie complète (4 focus)", "Full astrocartography (4 focuses)"],
  "pricing.premium.f2": ["Le Druide — chat IA illimité en mode tuteur", "The Druid — unlimited AI tutor chat"],
  "pricing.premium.f3": ["Formation druidique intégrée", "Built-in druidic training"],
  "pricing.premium.f4": ["Historique de vos lectures", "Reading history"],
  "pricing.premium.f5": ["Accès prioritaire aux nouveautés", "Priority access to updates"],
  "pricing.premium.buy": ["S'abonner · 15 $", "Subscribe · $15"],
  "pricing.dest.title": ["Destination Complète", "Full Destination"],
  "pricing.dest.sub": ["Paiement unique · par recherche", "One-time payment · per search"],
  "pricing.dest.cancel": ["Achetable à chaque nouvelle destination", "Buy for each new destination"],
  "pricing.dest.f1": ["Destination unique (voyage ou déménagement)", "Single destination (travel or move)"],
  "pricing.dest.f2": ["Guide de voyage sur mesure", "Tailored travel guide"],
  "pricing.dest.f3": ["Redirection immobilière", "Real estate guidance"],
  "pricing.dest.f4": ["Analyse culturelle du lieu", "Cultural analysis of the place"],
  "pricing.dest.f5": ["Énergie du lieu ↔ thème natal", "Place energy ↔ natal chart"],
  "pricing.dest.f6": ["Numérologie, sidéral, chinois, MC/IC", "Numerology, sidereal, Chinese, MC/IC"],
  "pricing.dest.buy": ["Acheter la lecture · 10 $", "Buy the reading · $10"],
  "pricing.footer": [
    "Paiement Stripe · Carte de test 4242 4242 4242 4242. Le Barde, les six oracles (20/j) et la Boule de Lumière restent toujours gratuits.",
    "Stripe payments · Test card 4242 4242 4242 4242. The Bard, the six oracles (20/day) and the Ball of Light remain always free.",
  ],
  "pricing.loading": ["Redirection…", "Redirecting…"],
  // Newsletter
  "news.kicker": ["Infolettre astrale", "Astral newsletter"],
  "news.title.a": ["Les nouvelles", "The new "],
  "news.title.b": ["lunes", "moons"],
  "news.title.c": [", dans votre boîte.", ", in your inbox."],
  "news.subtitle": [
    "Chaque nouvelle lune, recevez une lettre du Barde : philosophie, exercice druidique, invitation à vivre plutôt qu'à consulter. Aucun spam.",
    "At each new moon, receive a letter from the Bard: philosophy, druidic exercise, invitation to live rather than consult. No spam.",
  ],
  "news.badge": ["Protection CAPTCHA · honeypot · RGPD", "CAPTCHA · honeypot · GDPR"],
  "news.name": ["Votre nom", "Your name"],
  "news.email": ["votre@courriel.com", "you@email.com"],
  "news.captcha.label": ["Anti-robot", "Anti-robot"],
  "news.captcha.placeholder": ["Réponse", "Answer"],
  "news.submit": ["S'inscrire aux nouvelles lunes", "Subscribe to new moons"],
  "news.submit.loading": ["Envoi…", "Sending…"],
  "news.rgpd": ["RGPD niveau A1 · désinscription à tout moment · aucune vente de données", "GDPR level A1 · unsubscribe anytime · no data sale"],
  "news.success.title": ["Bienvenue.", "Welcome."],
  "news.success.body": ["Votre première lettre partira à la prochaine nouvelle lune.", "Your first letter will arrive at the next new moon."],
  // Footer
  "footer.tagline": [
    "AstroVoyance. Sagesse celtique et guidance philosophique — outils gratuits qui vous ramènent à votre libre-arbitre.",
    "AstroVoyance. Celtic wisdom and philosophical guidance — free tools that return you to your free will.",
  ],
  "footer.purge": ["Effacer & partir", "Erase & leave"],
  "footer.purge.confirm": [
    "Effacer toutes vos données locales et serveur, et revenir à l'accueil ?",
    "Erase all your local and server data and return to the home page?",
  ],
  "footer.col.consult": ["Consultations", "Consultations"],
  "footer.col.account": ["Compte", "Account"],
  "footer.col.legal": ["Légal", "Legal"],
  "footer.legal.privacy": ["Confidentialité A1", "Privacy A1"],
  "footer.legal.tos": ["CGU", "Terms"],
  "footer.legal.cookies": ["Cookies", "Cookies"],
  "footer.copyright": ["À but divertissant · 18 ans et +", "Entertainment purposes · 18+"],
  // Common
  "common.email.badge": ["Q", "Q"],
  "common.crown.premium": ["Premium", "Premium"],
  "common.error.email.required": ["Renseignez votre courriel en haut de page.", "Enter your email at the top of the page."],
  "common.error.premium.required": ["Abonnement Premium requis.", "Premium subscription required."],
  "common.error.retry": ["Une erreur est survenue. Réessayez.", "An error occurred. Please try again."],
  // Taverne / Pub du Barde
  "taverne.nav.badge": ["Pub", "Pub"],
  "taverne.kicker": ["Le Pub du Barde · musique celtique · poésie de guérison", "The Bard's Pub · celtic music · healing poetry"],
  "taverne.title.a": ["Entre au ", "Enter the "],
  "taverne.title.b": ["Pub", "Pub"],
  "taverne.title.c": [", écoute le Barde.", ", listen to the Bard."],
  "taverne.subtitle": [
    "Soixante-dix chants et slams du Barde — chants réflexifs sur les cinq sens et slams bardiques intenses (satiriques et thématiques) — pour soigner la raison par la poésie, sans jamais vous rendre dépendant d'un oracle. Voix masculine générée par votre navigateur, musique celtique libre de droits.",
    "Seventy chants and slams from the Bard — reflective songs on the five senses and intense bardic slams (satirical and thematic) — to heal reason through poetry, without ever making you dependent on an oracle. Masculine voice generated by your browser, royalty-free Celtic music.",
  ],
  "taverne.enter": ["Entrer au Pub", "Enter the Pub"],
  "taverne.filter.all": ["Tout", "All"],
  "taverne.filter.reflexif": ["Chants réflexifs", "Reflective chants"],
  "taverne.filter.slam": ["Slam bardique", "Bardic slam"],
  "taverne.filter.satirique": ["Satiriques uniquement", "Satirical only"],
  "taverne.usage.free": ["Écoutes gratuites aujourd'hui", "Free listens today"],
  "taverne.usage.premium": ["Illimité · Toujours gratuit", "Unlimited · Always free"],
  "taverne.usage.limit": ["Limite atteinte — passez Premium pour un accès illimité.", "Limit reached — upgrade to Premium for unlimited access."],
  "taverne.play": ["Écouter", "Listen"],
  "taverne.playing": ["Récitation en cours…", "Reciting…"],
  "taverne.stop": ["Arrêter", "Stop"],
  "taverne.music": ["Musique de fond", "Background music"],
  "taverne.music.on": ["Coupée", "Off"],
  "taverne.music.off": ["Activée", "On"],
  "taverne.category.reflexif": ["Chant réflexif", "Reflective chant"],
  "taverne.category.slam": ["Slam bardique", "Bardic slam"],
  "taverne.subcat.satirique": ["Satire anti-voyance", "Anti-clairvoyance satire"],
  "taverne.subcat.thematique": ["Thème universel", "Universal theme"],
  "taverne.sense.vue": ["La vue", "Sight"],
  "taverne.sense.ouie": ["L'ouïe", "Hearing"],
  "taverne.sense.toucher": ["Le toucher", "Touch"],
  "taverne.sense.gout": ["Le goût", "Taste"],
  "taverne.sense.odorat": ["L'odorat", "Smell"],
  "taverne.empty": ["Aucun poème pour ce filtre.", "No poems for this filter."],
  "taverne.back": ["Retour à l'accueil", "Back to home"],
  "taverne.upgrade.cta": ["Passer Premium (15 $/mois)", "Go Premium ($15/mo)"],
  "taverne.notice.speech": [
    "Astuce : votre navigateur récite les poèmes avec une voix bardique. Sur Chrome/Edge, la voix française est plus naturelle. Ajustez le volume si besoin.",
    "Tip: your browser recites the poems with a bardic voice. On Chrome/Edge, the French voice is more natural. Adjust volume if needed.",
  ],
  // Le Vate — Coach Relationnel et Émotionnel (18+ obligatoire)
  "vate.kicker": ["Le Vate · coach relationnel et émotionnel", "The Vate · relational & emotional coach"],
  "vate.title.a": ["Le ", "The "],
  "vate.title.b": ["Vate", "Vate"],
  "vate.title.c": [", panseur d'âme.", ", soul-mender."],
  "vate.subtitle": [
    "Écoute confidentielle, sans jugement, ni prescription. Le Vate est un coach relationnel et émotionnel de la tradition celtique — pas un thérapeute. Ce qu'il t'offre : une présence, un miroir, parfois un rire qui panse. Gratuit, anonyme, chiffré.",
    "Confidential listening, without judgment or prescription. The Vate is a relational & emotional coach of the Celtic tradition — not a therapist. What they offer: a presence, a mirror, sometimes a laughter that heals. Free, anonymous, encrypted.",
  ],
  "vate.gate.title": ["Avant de t'asseoir près du feu", "Before you sit by the fire"],
  "vate.gate.body": [
    "Le Vate ne reçoit que les âmes majeures — la parole y est libre, mature, sans détour. En entrant tu confirmes avoir 18 ans révolus. Aucune donnée personnelle n'est demandée, ta session est chiffrée et effaçable à tout moment.",
    "The Vate only receives adult souls — speech here is free, mature, unfiltered. By entering you confirm you are 18 or older. No personal data is requested; your session is encrypted and wipeable at any time.",
  ],
  "vate.gate.enter": ["J'ai 18 ans ou plus — Entrer près du feu", "I am 18 or older — Enter by the fire"],
  "vate.gate.decline": ["Revenir à l'accueil", "Return home"],
  "vate.mode.badge": ["Séance 18+ · chiffrée", "Session 18+ · encrypted"],
  "vate.placeholder": ["Confie-lui ce qui pèse sur ton cœur…", "Confide what weighs on your heart…"],
  "vate.send": ["Envoyer", "Send"],
  "vate.clear": ["Effacer & partir", "Wipe & leave"],
  "vate.suggestions.title": ["Ou choisis un souffle pour commencer :", "Or choose a breath to begin:"],
  "vate.suggestion.1": ["Je viens de rompre. Comment tenir la nuit ?", "I just broke up. How do I hold the night?"],
  "vate.suggestion.2": ["Je répète les mêmes relations toxiques.", "I keep repeating the same toxic relationships."],
  "vate.suggestion.3": ["Comment reconnaître un manipulateur ?", "How do I recognize a manipulator?"],
  "vate.suggestion.4": ["J'ai peur d'aimer à nouveau.", "I'm afraid to love again."],
  "vate.suggestion.5": ["Je me sens invisible dans le dating.", "I feel invisible in the dating scene."],
  "vate.suggestion.6": ["Mon anxiété m'empêche de dormir.", "My anxiety keeps me from sleeping."],
  "vate.disclaimer.title": ["Le Vate n'est pas un professionnel de santé.", "The Vate is not a health professional."],
  "vate.disclaimer.body": [
    "En cas de détresse aiguë, contactez immédiatement : 🇫🇷 3114 · 🇨🇦 1-833-456-4566 · 🇺🇸 988 · 🇬🇧 116 123. Le Vate offre une écoute, pas un soin. Il ne pose aucun diagnostic et ne prescrit rien. Rôle purement éducatif.",
    "In case of acute distress, contact immediately: 🇫🇷 3114 · 🇨🇦 1-833-456-4566 · 🇺🇸 988 · 🇬🇧 116 123. The Vate offers a listening ear, not medical care. No diagnosis, no prescription. Purely educational role.",
  ],
  "vate.crisis.banner": [
    "🕯️ Tu comptes. Un être humain est à un appel de toi : 3114 (FR) · 988 (US) · 1-833-456-4566 (CA) · 116 123 (UK). Appelle.",
    "🕯️ You matter. A human is one call away: 988 (US) · 116 123 (UK) · 1-833-456-4566 (CA) · 3114 (FR). Call.",
  ],
  "vate.privacy.badge": ["Session · chiffrée · effaçable", "Session · encrypted · wipeable"],
  "vate.back": ["Retour à l'accueil", "Back to home"],
};

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(KEY) || "fr");

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    const v = next === "en" ? "en" : "fr";
    setLangState(v);
    localStorage.setItem(KEY, v);
  }, []);

  const t = useCallback(
    (key) => {
      const entry = DICT[key];
      if (!entry) return key;
      return entry[lang === "en" ? 1 : 0] ?? entry[0];
    },
    [lang]
  );

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export const useLang = () => useContext(Ctx) || { lang: "fr", setLang: () => {}, t: (k) => k };
